import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { Accordion, LoadingDots } from '@eti/components';
import { Card, Stack, Text, textVariants, vars } from '@etg/wings';
import type { TravelDocsInfo } from '@eti/schema-types';
import TitledCard from '../../../../common/components/TitledCard';
import { bulletStyles } from '../../../../utils/styleUtils';
import { getTripInformationDocuments } from './utils';

const accordionWrapperStyles = css`
  border: 1px solid ${vars.colors.divider};
  border-radius: ${vars.card.borderRadius};
`;

const loadingDotsStyles = css`
  font-size: 2.25rem;
  text-align: center;
`;

interface TravelDocumentsAndVisaProps {
  travelDocsInfo?: TravelDocsInfo | null;
  hasError: boolean;
  isLoading: boolean;
}

const TravelDocumentsAndVisa = ({
  travelDocsInfo,
  hasError,
  isLoading,
}: TravelDocumentsAndVisaProps) => {
  const { t } = useTranslation();
  const tripInformationDocuments = hasError
    ? [
        {
          dataTestId: 'document-us-expansion-panel',
          title: t('OrderDetails.TravelInformation.Documents.US.Title'),
          description: t('OrderDetails.TravelInformation.Documents.US.Description'),
        },
        {
          dataTestId: 'document-hkg-expansion-panel',
          title: t('OrderDetails.TravelInformation.Documents.HKG.Title'),
          description: t('OrderDetails.TravelInformation.Documents.HKG.Description'),
        },
        {
          dataTestId: 'document-ca-expansion-panel',
          title: t('OrderDetails.TravelInformation.Documents.CA.Title'),
          description: t('OrderDetails.TravelInformation.Documents.CA.Description'),
        },
        {
          dataTestId: 'document-apis-expansion-panel',
          title: t('OrderDetails.TravelInformation.Documents.APIS.Title'),
          description: t('OrderDetails.TravelInformation.Documents.APIS.Description'),
        },
      ]
    : getTripInformationDocuments(travelDocsInfo);

  const isPanelInitiallyOpen = tripInformationDocuments.length === 1;

  return (
    <TitledCard
      data-testid="travel-documents-and-visa"
      title={t('OrderDetails.TravelInformation.DocumentsAndVisa.Title')}
    >
      <Stack>
        <Text asHtml className={bulletStyles()}>
          {t('OrderDetails.TravelInformation.DocumentsAndVisa.Content')}
        </Text>
        {isLoading ? (
          <LoadingDots containerStyle={loadingDotsStyles} />
        ) : (
          <Stack>
            {tripInformationDocuments.map((document) => (
              <Card className={accordionWrapperStyles} key={document.dataTestId}>
                <Accordion
                  id="travel-documents-and-visa-accordion"
                  isInitiallyExpanded={isPanelInitiallyOpen}
                  key={document.dataTestId}
                  variant="full-width"
                >
                  <Accordion.Header data-testid={document.dataTestId}>
                    <Text variant={textVariants._16_BOLD}>{document.title}</Text>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Text asHtml data-testid={`${document.dataTestId}-body-text`}>
                      {document.description}
                    </Text>
                  </Accordion.Body>
                </Accordion>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </TitledCard>
  );
};

export default TravelDocumentsAndVisa;
